import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { Profile } from "../../domain/entities/Profile";
import MailIcon from "@/assets/icons/mail.svg?react";
import MobileIcon from "@/assets/icons/call.svg?react";
import ClockIcon from "@/assets/icons/clock.svg?react";
import RolesIcon from "@/assets/icons/Roles.svg?react";
import { textStyles } from "@/lib/textStyles";

interface ProfileCardProps {
  profile: Profile;
  onEdit: () => void;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
}) => {
  const { t } = useTranslation("profile");
  const initials = profile.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="rounded-xl border p-4">
      <div className="flex min-w-0 items-start gap-3 sm:gap-4">
        <Avatar className="h-14 w-14 shrink-0">
          <AvatarFallback style={textStyles.heading2}>
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p style={textStyles.heading3}>{profile.name}</p>
            <span
              className={`inline-flex w-fit items-center justify-center rounded-lg px-2 py-[5px] ${
                profile.status === "Active"
                  ? "bg-green-100 text-green-600"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              <span style={textStyles.body}>{profile.status}</span>
            </span>
          </div>

          <div
            className="mt-1 flex flex-col gap-1.5 text-gray-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1"
            style={textStyles.body}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span aria-hidden="true" className="shrink-0 text-gray-400">
                •
              </span>
              <MailIcon className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="break-all sm:break-normal">{profile.email}</span>
            </span>

            <span className="flex min-w-0 items-center gap-1.5">
              <span aria-hidden="true" className="shrink-0 text-gray-400">
                •
              </span>
              <MobileIcon className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="break-all sm:break-normal">{profile.phone}</span>
            </span>

            {profile.roleName && (
              <span className="flex min-w-0 items-center gap-1.5">
                <span aria-hidden="true" className="shrink-0 text-gray-400">
                  •
                </span>
                <RolesIcon className="h-4 w-4 shrink-0 text-gray-400" />
                <span>{profile.roleName}</span>
              </span>
            )}

            {profile.role && (
              <span className="flex min-w-0 items-center gap-1.5">
                <span aria-hidden="true" className="shrink-0 text-gray-400">
                  •
                </span>
                {!profile.roleName && (
                  <RolesIcon className="h-4 w-4 shrink-0 text-gray-400" />
                )}
                <span>
                  {profile.roleName
                    ? t("designation", { role: profile.role })
                    : profile.role}
                </span>
              </span>
            )}

            <span className="flex min-w-0 items-center gap-1.5">
              <span aria-hidden="true" className="shrink-0 text-gray-400">
                •
              </span>
              <ClockIcon className="h-4 w-4 shrink-0 text-gray-400" />
              <span>{t("createdOn", { date: profile.createdAt })}</span>
            </span>
          </div>
        </div>
      </div>

      {/* <Button variant="ghost" onClick={onEdit} className="cursor-pointer">
        <EditIcon className="w-5 h-5" />
      </Button> */}
    </div>
  );
};
